import React, { FC, memo } from 'react';
import { OrderHistoryItem } from '~/lib/data-contract/auto-generated/customer-model';
import styles from './order-details.module.css';
import { Typography } from '~/shared/components';
import Product from './product/product.component';
import { useTranslation } from '~/shared/utils';

type Props = {
    order?: OrderHistoryItem | null;
};

const OrderDetails: FC<Props> = memo(({ order }) => {
    const { translate } = useTranslation();

    if (!order) {
        return null;
    }

    const {
        totalQuantity,
        displayVoucherAmount,
        displayGiftCardsRedeemed,
        displayBonusUsed,
        freeShipping,
        displayShipping,
        displayTotalPrice,
        displayBonusEarned,
        purchaseSourceType
    } = order;


    const isOnlineOrder = purchaseSourceType === 'Online';

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div>
                    <Typography variant="bodyXs" as="div" className={styles.label}>
                        {translate('club.orderHistory.purchaseId')}
                    </Typography>
                    <Typography variant="bodySm" as="div" fontWeight="semibold">
                        {order.purchaseId}
                    </Typography>
                </div>
                <div>
                    <Typography variant="bodyXs" as="div" className={styles.label}>
                        {translate('club.orderHistory.orderDate')}
                    </Typography>
                    <Typography variant="bodySm" as="div" fontWeight="semibold">
                        {order.displayDate}
                    </Typography>
                </div>
            </div>
            <div className={styles.divider} />
            <div>
                <Typography variant="display6" as="h3" fontWeight="semibold">
                    {isOnlineOrder
                        ? translate('club.orderHistory.onlineOrder')
                        : order.purchaseSourceName || translate('club.orderHistory.inStoreOrder')}
                </Typography>
                <Typography variant="body" as="span" className={styles.itemCount}>
                    {totalQuantity}{' '}
                    {translate(`club.orderHistory.${totalQuantity === 1 ? 'item' : 'items'}`)}
                </Typography>
            </div>
            <div className={styles.productsList}>
                {order.products &&
                    order.products.length > 0 &&
                    order.products.map((product, index) => (
                        <Product
                            key={`${product.productId}-${index}`}
                            product={product}
                        />
                    ))}
            </div>
            <div className={styles.divider} />
            <div className={styles.summary}>
                <div className={styles.summaryRow}>
                    <Typography variant="bodyLg" fontWeight="bold" as="div">
                        {translate('club.orderHistory.totalPrice')}
                    </Typography>
                    <Typography variant="bodyLg" fontWeight="bold" as="div">
                        {displayTotalPrice}
                    </Typography>
                </div>
                {displayBonusEarned && (
                    <div className={styles.summaryRow}>
                        <Typography variant="bodySm" as="div">
                            {translate('club.orderHistory.totalBonusEarned')}:
                        </Typography>
                        <Typography variant="bodySm" as="div">
                            {displayBonusEarned}
                        </Typography>
                    </div>
                )}
                {displayVoucherAmount && (
                    <div className={styles.summaryRow}>
                        <Typography variant="bodySm" as="span">
                            {translate('club.orderHistory.voucherAmount')}:
                        </Typography>
                        <Typography variant="bodySm" as="span">
                            {displayVoucherAmount}
                        </Typography>
                    </div>
                )}
                {displayGiftCardsRedeemed && (
                    <div className={styles.summaryRow}>
                        <Typography variant="bodySm" as="span">
                            {translate('club.orderHistory.giftCardsRedeemed')}:
                        </Typography>
                        <Typography variant="bodySm" as="span">
                            {displayGiftCardsRedeemed}
                        </Typography>
                    </div>
                )}
                {displayBonusUsed && (
                    <div className={styles.summaryRow}>
                        <Typography variant="bodySm" as="span">
                            {translate('club.orderHistory.bonus')}:
                        </Typography>
                        <Typography variant="bodySm" as="span">
                            {displayBonusUsed}
                        </Typography>
                    </div>
                )}
                {isOnlineOrder ? (
                    <div className={styles.summaryRow}>
                        <Typography variant="bodySm" as="span">
                            {translate('club.orderHistory.shipping')}:
                        </Typography>
                        <Typography variant="bodySm" as="span">
                            {freeShipping
                                ? translate('club.orderHistory.freeShipping')
                                : displayShipping}
                        </Typography>
                    </div>
                ) : null}
            </div>
        </div>
    );
});

OrderDetails.displayName = 'OrderDetails';
export default OrderDetails;
